<template>
  <header
    v-if="controls"
    class="desktop-window-bar"
    :class="{ 'desktop-window-bar--auth': isAuth }"
    data-theme-surface="header"
  >
    <div class="desktop-window-drag" @dblclick="toggleMaximize">
      <span v-if="!isAuth">灵犀工坊</span>
    </div>
    <div class="desktop-window-actions">
      <button
        v-if="!isAuth && browser"
        class="browser-button"
        type="button"
        :title="desktopStore.browserError || '打开灵犀浏览器'"
        aria-label="打开灵犀浏览器"
        @click="desktopStore.openBrowser()"
      >
        <i class="ri-global-line" /><span>浏览器</span>
      </button>
      <button
        v-if="!isAuth"
        type="button"
        aria-label="最小化"
        title="最小化"
        @click="controls.minimize()"
      >
        <i class="ri-subtract-line" />
      </button>
      <button
        v-if="!isAuth"
        type="button"
        :aria-label="maximized ? '还原窗口' : '最大化'"
        :title="maximized ? '还原窗口' : '最大化'"
        @click="toggleMaximize"
      >
        <i :class="maximized ? 'ri-file-copy-line' : 'ri-checkbox-blank-line'" />
      </button>
      <button
        class="desktop-window-close"
        type="button"
        aria-label="关闭"
        title="关闭"
        @click="controls.close()"
      >
        <i class="ri-close-line" />
      </button>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { getDesktopBridge } from '@/desktop';
import { useDesktopStore } from '@/stores/desktop.store';

const controls = getDesktopBridge()?.window;
const browser = getDesktopBridge()?.browser;
const desktopStore = useDesktopStore();
const route = useRoute();
const isAuth = computed(() => route.path.startsWith('/auth'));
const maximized = ref(false);
let stop: (() => void) | undefined;
async function toggleMaximize(): Promise<void> {
  if (controls) maximized.value = await controls.toggleMaximize();
}
onMounted(async () => {
  if (!controls) return;
  stop = controls.onMaximizedChange((value) => {
    maximized.value = value;
  });
  maximized.value = await controls.isMaximized();
});
onBeforeUnmount(() => stop?.());
</script>

<style scoped>
.desktop-window-bar {
  position: fixed;
  z-index: 1100;
  inset: 0 0 auto;
  display: flex;
  height: var(--desktop-window-bar-height);
  color: var(--ws-color-text-muted);
  background: var(--ws-color-surface);
  border-bottom: 1px solid var(--ws-color-border);
  user-select: none;
}
.desktop-window-drag {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding-inline: 20px;
  font-size: 12px;
  -webkit-app-region: drag;
}
.desktop-window-actions {
  display: flex;
  -webkit-app-region: no-drag;
}
.desktop-window-actions button {
  display: grid;
  width: 46px;
  height: 100%;
  place-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--ws-color-text);
  cursor: pointer;
  font-size: 15px;
}
.desktop-window-actions button:hover {
  background: var(--ws-color-surface-muted);
}
.desktop-window-actions button:focus-visible {
  outline: 2px solid var(--ws-color-focus);
  outline-offset: -2px;
}
.desktop-window-actions .browser-button {
  display: flex;
  gap: 5px;
  width: auto;
  padding-inline: 12px;
  font-size: 12px;
}
.desktop-window-actions .desktop-window-close:hover {
  background: var(--ws-color-danger);
  color: var(--aw-on-primary);
}
.desktop-window-bar--auth {
  height: 40px;
  background: transparent;
  border: 0;
}
.desktop-window-bar--auth .desktop-window-actions {
  padding: 8px 12px 0 0;
}
.desktop-window-bar--auth .desktop-window-actions button {
  width: 32px;
  height: 32px;
  border-radius: var(--aw-radius-xs);
}
</style>
